import "@/App.css";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Analytics } from "@vercel/analytics/react";
import AppLayout, { AppShell } from "@/components/AppLayout";

/*
 * Pages load on demand.
 *
 * Every page used to sit in the entry chunk, so a driver opening the dashboard
 * first downloaded the admin panel, the landing page and three separate copies
 * of the chart library before anything rendered. Splitting per route means you
 * fetch the screen you asked for and nothing else.
 *
 * The shell above stays eager: it renders while a page is still arriving.
 */
const LandingPage = lazy(() => import(/* webpackChunkName: "landing" */ "@/pages/LandingPage"));
const AuthPage = lazy(() => import(/* webpackChunkName: "auth" */ "@/pages/AuthPage"));
const ResetPasswordPage = lazy(() => import(/* webpackChunkName: "reset-password" */ "@/pages/ResetPassword"));
const Dashboard = lazy(() => import(/* webpackChunkName: "dashboard" */ "@/pages/Dashboard"));
const Statistieken = lazy(() => import(/* webpackChunkName: "statistieken" */ "@/pages/Statistieken"));
const Leaderboards = lazy(() => import(/* webpackChunkName: "leaderboard" */ "@/pages/Leaderboards"));
const AddRide = lazy(() => import(/* webpackChunkName: "add-ride" */ "@/pages/AddRide"));
const RideHistory = lazy(() => import(/* webpackChunkName: "ride-history" */ "@/pages/RideHistory"));
const Settings = lazy(() => import(/* webpackChunkName: "settings" */ "@/pages/Settings"));
const AdminPanel = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/AdminPanel"));
import PageSkeleton from "@/components/app/PageSkeleton";
import Footer from "@/components/Footer";
import InstallPrompt from "@/components/InstallPrompt";
import { LoadingStatusPill } from "@/components/PageLoaders";

function PublicRouteLoader() {
  return (
    <div className="min-h-[100dvh] bg-dd-bg flex items-center justify-center px-4">
      <LoadingStatusPill text="Sessie herstellen..." className="px-4 py-2.5 text-sm" />
    </div>
  );
}

/* The shell is static, so it renders immediately and only the page body waits. */
function AuthenticatedLayoutLoader() {
  return (
    <AppShell>
      <PageSkeleton />
    </AppShell>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <AuthenticatedLayoutLoader />;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <AuthenticatedLayoutLoader />;
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicOnlyRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <PublicRouteLoader />;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  /*
   * Outer boundary for the public pages. The routed pages under AppLayout have
   * their own boundary inside the shell, which catches first, so this one only
   * ever covers the landing, auth and recovery screens.
   */
  return (
    <Suspense fallback={<PublicRouteLoader />}>
      <Routes>
        <Route path="/" element={loading ? <PublicRouteLoader /> : user ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
        <Route path="/login" element={<Navigate to="/auth" replace />} />
        <Route path="/auth" element={<PublicOnlyRoute><AuthPage /></PublicOnlyRoute>} />
        <Route path="/auth/recovery" element={<ResetPasswordPage />} />
        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/statistieken" element={<Statistieken />} />
          <Route path="/leaderboard" element={<Leaderboards />} />
          <Route path="/cars" element={<Navigate to="/statistieken" replace />} />
          <Route path="/rides/add" element={<AddRide />} />
          <Route path="/rides/edit/:rideId" element={<AddRide />} />
          <Route path="/rides" element={<RideHistory />} />
          <Route path="/settings" element={<Settings />} />
        </Route>
        <Route element={<AdminRoute><AppLayout /></AdminRoute>}>
          <Route path="/admin" element={<AdminPanel />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

/*
 * A route change keeps the window's scroll position, so opening a page from a
 * button halfway down a list drops you halfway down the next page. Reset it.
 *
 * Back and forward are left alone: there the browser restores where you were,
 * which is what you want when returning to a long list.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === "POP") return;
    window.scrollTo(0, 0);
  }, [pathname, navigationType]);

  return null;
}

function App() {
  const showAnalytics = process.env.NODE_ENV === "production";

  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <div className="flex flex-col min-h-[100dvh] bg-dd-bg">
          <div className="flex-1">
            <AppRoutes />
          </div>
          <Footer />
          <Toaster
            position="top-right"
            toastOptions={{
              style: { background: "var(--dd-surface-1)", border: "1px solid var(--dd-line)", color: "var(--dd-text)" },
            }}
          />
          <InstallPrompt />
          {showAnalytics ? <Analytics /> : null}
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
